import type { ReportLatestRuns } from 'api/analytics-reports/types';
import FilesUtil from 'utils/files';

export function isHttpUrl(url: string | null | undefined): url is string {
  if (!url) {
    return false;
  }
  return url.startsWith('http://') || url.startsWith('https://');
}

export function isAnalyticsPdfExportCompleted(
  latestRuns: ReportLatestRuns | undefined,
): boolean {
  return latestRuns?.pdf?.status === 'completed';
}

export function resolveAnalyticsReportPdfDownloadUrl(
  latestRuns: ReportLatestRuns | undefined,
): string | null {
  if (!isAnalyticsPdfExportCompleted(latestRuns)) {
    return null;
  }

  const url = latestRuns?.pdf?.download_url;
  return isHttpUrl(url) ? url : null;
}

function sanitizeReportFileName(name: string): string {
  return name
    .replace(/[–—]/g, '-')
    .replace(/[^a-zA-Z0-9_\-. ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildReportPdfFileName(
  reportName: string | undefined,
  reportId: number,
): string {
  if (!reportName) {
    return `${reportId}.pdf`;
  }
  const sanitized = sanitizeReportFileName(reportName);
  return `${sanitized || reportId}.pdf`;
}

export function buildReportCsvFileName(
  reportName: string | undefined,
  reportId: number,
): string {
  if (!reportName) {
    return `${reportId}.csv`;
  }
  const sanitized = sanitizeReportFileName(reportName);
  return `${sanitized || reportId}.csv`;
}

export function buildReportExportFileName(
  reportName: string | undefined,
  reportId: number,
  format: 'pdf' | 'csv',
): string {
  return format === 'pdf'
    ? buildReportPdfFileName(reportName, reportId)
    : buildReportCsvFileName(reportName, reportId);
}

export function openReportPdfInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function downloadReportPdfFromUrl(
  fileUrl: string,
  fileName: string,
): void {
  FilesUtil.triggerBrowserDownload(fileUrl, fileName);
}

export async function downloadReportExport(
  fileUrl: string,
  fileName: string,
): Promise<void> {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    FilesUtil.triggerBrowserDownload(blobUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    openReportPdfInNewTab(fileUrl);
  }
}
